import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, IonSelect, ModalController, PopoverController } from '@ionic/angular';
import { Logger } from 'src/app/api/helper/app-error-logger';
import { AuftragHelper } from 'src/app/api/helper/auftrag-helper';
import { Adresse, AuftragsKomponente, Textdatei, TextdateiTyp, WiegungTyp } from 'src/app/api/model/swagger-model';
import { Auftragdetails } from '../../auftragdetails.helper';
import { KomponenteBase } from '../komponente';
import { AuftragService } from 'src/app/api/auftrag.service';
import { DomSanitizer } from '@angular/platform-browser';
import { AuftragspositionEx, AuftragspositionWiegungEx, WaageZuordnungTyp } from 'src/app/api/model/model';
import { WaageService } from 'src/app/api/waage.service';
import { UiHelper } from 'src/app/api/helper/ui-helper';
import { MyApp } from 'src/app/api/helper/app';
import { Utils } from 'src/app/api/helper/utils';
import { AppConfig } from 'src/app/api/helper/app.config';
import { SystemService } from 'src/app/api/system.service';


const log = new Logger("AuftragPelletsEinblasenKomponenteComponent");

@Component({
    selector: 'auftrag-pellets-einblasen-komponente',
    templateUrl: './auftrag-pellets-einblasen-komponente.component.html',
    styleUrls: ['./auftrag-pellets-einblasen-komponente.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AuftragPelletsEinblasenKomponenteComponent extends KomponenteBase implements OnInit, OnChanges, OnDestroy {

    @ViewChild('artWaehlenSelect', { read: IonSelect, static: false }) artWaehlenSelect: IonSelect;

    @Input() auftragdetails: Auftragdetails;
    @Input() komponente: AuftragsKomponente;

    @Output() lieferantChanged: EventEmitter<Adresse> = new EventEmitter();

    wiegung1: AuftragspositionWiegungEx = {
        Nummer: 1,
        Bezeichnung: 'Erstwiegung',
        Erfolgreich: false
    };

    wiegung2: AuftragspositionWiegungEx = {
        Nummer: 2,
        Bezeichnung: 'Zweitwiegung',
        Erfolgreich: false
    };

    wiegung3: AuftragspositionWiegungEx = {
        Nummer: 3,
        Bezeichnung: 'Erstwiegung Anhänger',
        Erfolgreich: false
    };

    wiegung4: AuftragspositionWiegungEx = {
        Nummer: 4,
        Bezeichnung: 'Zweitwiegung Anhänger',
        Erfolgreich: false
    };

    nettoGewicht = 0;

    isBearbeitenErlaubt = true;

    /**
     * Die Auftragsposition, die die Pellets enthält.
     */
    pelletsAuftragsposition: AuftragspositionEx = null;

    isAnhangerWaageVorhanden = false;

    buttonSize = 'large';

    buttonExpand = 'block';

    isHandeingabeErlaubt = false;

    constructor(
        private router: Router,
        private modalController: ModalController,
        private auftragService: AuftragService,
        private popoverController: PopoverController,
        private alertController: AlertController,
        private waageService: WaageService,
        private systemService: SystemService,
        public domSanitizer: DomSanitizer,
        protected cdr: ChangeDetectorRef) {
        super(cdr);
    }

    ngOnInit() {
        super.ngOnInit();

        this.init();

        this.aktualisieren();

        this.subscriptions.push(this.auftragdetails.detectChanges.subscribe(() => {
            this.isHandeingabeErlaubt = this.auftragdetails.isHandeingabeErlaubt;

            this.detectChanges();
        }))
    }

    detectChanges() {
        try {
            this.cdr.detectChanges();
        } catch (err) {
            log.warn('detectChanges: ' + Utils.getErrorMessage(err), err);
        }
    }

    init() {
        if (!this.auftrag) {
            return;
        }

        this.updateBearbeitenErlaubt();

        this.isAnhangerWaageVorhanden = this.waageService.getAktiveWaageKonfigurationen().some(p => p.zuordnung === 'Anhaenger');

        this.buttonSize = this.isAnhangerWaageVorhanden ? 'default' : 'large';

        this.isHandeingabeErlaubt = this.auftragdetails.isHandeingabeErlaubt;
    }

    aktualisieren() {
        this.pelletsAuftragsposition = null;

        // Wie Wiegungen werden immer aus der ersten Auftragsposition gelesen.
        // Das sollte immer die Pellets-Position sein.
        if (this.auftrag && this.auftrag.Positionen && this.auftrag.Positionen.length > 0) {
            const pos = this.auftrag.Positionen[0];

            this.pelletsAuftragsposition = pos;

            if (pos.Wiegungen2?.length) {
                this.wiegung1 = pos.Wiegungen2.find(w => w.Nummer === 1) || this.wiegung1;
                this.wiegung2 = pos.Wiegungen2.find(w => w.Nummer === 2) || this.wiegung2;
                this.wiegung3 = pos.Wiegungen2.find(w => w.Nummer === 3) || this.wiegung3;
                this.wiegung4 = pos.Wiegungen2.find(w => w.Nummer === 4) || this.wiegung4;
            }
        }

        this.auftrag.WiegungErfolgreich = this.wiegung1?.Erfolgreich && this.wiegung2?.Erfolgreich;

        this.updateNetto();
    }

    ngOnDestroy() {
        log?.debug('ngOnDestroy');

        this.subscriptions.forEach(s => s.unsubscribe());
        this.subscriptions = [];
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.init();
    }

    updateBearbeitenErlaubt() {
        this.isBearbeitenErlaubt = false;

        if (!this.auftrag) {
            return;
        }

        if (AuftragHelper.isAuftragImArchiv(this.auftrag)) {
            return;
        }

        if (this.auftragdetails.isReadOnly) {
            return;
        }

        this.isBearbeitenErlaubt = true;
    }

    async onErstwiegung(zuordnung: WaageZuordnungTyp) {
        log?.debug('onErstwiegung');

        try {
            if (zuordnung === 'Anhaenger') {
                await this.doWiegen(3, 11_000 + Math.floor(Math.random() * 2000));
            } else {
                // Fahrzeug
                await this.doWiegen(1, 18_250 + Math.floor(Math.random() * 2000));
            }

        } catch (err) {
            UiHelper.showError(err);
        } finally {
            MyApp.loading(false);

            this.cdr.detectChanges();
        }
    }

    async onZweitwiegung(zuordnung: WaageZuordnungTyp) {
        log?.debug('onZweitwiegung');

        try {
            if (zuordnung === 'Anhaenger') {
                await this.doWiegen(4, 4_000);
            } else {
                // Fahrzeug
                await this.doWiegen(2, 11_125);
            }

        } catch (err) {
            UiHelper.showError(err);
        } finally {
            MyApp.loading(false);

            this.cdr.detectChanges();
        }
    }


    async onHandeingabe(nr: number) {
        log?.debug('onHandeingabe: ' + nr);

        try {
            const str = await UiHelper.inputPIN('Gewicht in kg');

            if (!str) {
                return;
            }

            const gewicht = Utils.parseInt(str);

            if (gewicht) {
                const wiegung = this.getWiegung(nr);

                wiegung.Erfolgreich = true;
                wiegung.Datum = Utils.nowIsoDateString();
                wiegung.Text = Utils.formatFloat(gewicht, 0) + ' kg (Handeingabe)';
                wiegung.Gewicht = gewicht;
                wiegung.WiegeNr = '****';
                wiegung.Typ = WiegungTyp.Handeingabe;

                if (nr === 2 || nr === 4) {
                    this.updateNetto();

                    this.aktualisiereMengeInAuftragsposition();
                }

                await this.speichern();

                this.cdr.detectChanges();
            }

        } catch (err) {
            UiHelper.showError(err);
        } finally {
            MyApp.loading(false);

            this.cdr.detectChanges();
        }
    }


    async doWiegen(nr: number, simulationGewicht?: number) {
        let zuordnung: WaageZuordnungTyp = 'Fahrzeug';

        if (nr === 3 || nr === 4) {
            zuordnung = 'Anhaenger';
        }

        const waage = await this.waageService.getAktiveWaage(zuordnung);

        if (!waage) {
            UiHelper.showErrorLight('Keine Waage verbunden');
            return;
        }

        MyApp.loading(true);

        waage.simulationGewicht = simulationGewicht;

        const wiegeergebnis = await waage.einzelwiegung();

        if (!wiegeergebnis) {
            UiHelper.showErrorLight('Keine Wiegedaten empfangen');
            return;
        }

        if (wiegeergebnis.errorMessage) {
            UiHelper.showErrorLight(wiegeergebnis.errorMessage);
            return;
        }

        const wiegung = this.getWiegung(nr);

        wiegung.Erfolgreich = true;
        wiegung.Datum = Utils.nowIsoDateString();
        wiegung.Text = Utils.formatFloat(wiegeergebnis.Wiegung1, 0) + ' kg';
        wiegung.Gewicht = wiegeergebnis.Wiegung1;
        wiegung.Nummer = nr;
        wiegung.WiegeNr = wiegeergebnis.Wiegenummer?.toString();
        wiegung.Typ = WiegungTyp.Wiegung;

        if (nr === 2 || nr === 4) {
            this.updateNetto();

            this.aktualisiereMengeInAuftragsposition();
        }

        await this.speichern();

        this.cdr.detectChanges();

        try {
            // Sende die Wiegeinformationen an den Server
            const now = Utils.nowIsoDateString();

            let auftragsposition = this.auftrag.Positionen?.find(p => p.AuftragWiegungAktiv);

            if (!auftragsposition && this.auftrag.Positionen?.length) {
                auftragsposition = this.auftrag.Positionen[0];
            }

            const textdatei: Textdatei = {
                typ: TextdateiTyp.Wiegung,
                datum: now,
                geraeteNummer: AppConfig.current.geraeteNummer,
                Wiegung: {
                    Guid: Utils.uuid(),

                    // Informationen zum Auftrag, Fahrzeug, Fahrer etc.
                    AuftragKey: this.auftrag.Key,
                    Auftragsnummer: this.auftrag.Auftragsnummer,
                    Anhaenger: MyApp.current.getAnhaengerKennzeichen(),
                    AnhaengerKey: MyApp.current.getAnhaengerKey(),
                    Fahrzeug: MyApp.current.getFahrzeugKennzeichen(),
                    FahrzeugKey: MyApp.current.getFahrzeugKey(),
                    Position: this.systemService.letzteGpsPosition,
                    PersonalKey: MyApp.current.getPersonalKey(),
                    FahrerName: MyApp.current.getPersonalName(),
                    BeifahrerName: '',
                    Erzeugeradresse: this.auftrag.Erzeugeradresse,
                    Abholadresse: this.auftrag.Abholadresse,
                    Auftragsposition: auftragsposition,

                    // Die eigentliche Wiegung
                    Datum: wiegung.Datum,
                    Typ: wiegung.Typ,
                    Gewicht: wiegung.Gewicht,
                    WiegeNr: wiegung.WiegeNr,
                    Nummer: wiegung.Nummer,
                    Einheit: wiegung.Einheit,
                    GeraeteNummer: AppConfig.current.geraeteNummer,
                    WaageKey: waage.konfiguration.typ,
                    WaageMacAdresse: '',
                    WaageName: waage.konfiguration.name,
                    WaageTyp: waage.konfiguration.typ,
                    WaageZuordnung: waage.konfiguration.zuordnung,
                }
            };

            // Hier extra kein Await. Damit wird der Versand im Hintergrund durchgeführt und kein Ladesystem angezeigt.
            this.systemService.sendeTextdatei(textdatei, true)
        } catch (err) {
            log.error('doWiegen: ' + Utils.getErrorMessage(err), err);
        }
    }

    getWiegung(nr: number) {
        switch (nr) {
            case 1: return this.wiegung1;
            case 2: return this.wiegung2;
            case 3: return this.wiegung3;
            case 4: return this.wiegung4;
        }

        return null;
    }

    aktualisiereMengeInAuftragsposition() {
        if (!this.pelletsAuftragsposition) {
            log.warn('aktualisiereMengeInAuftragsposition: Keine Pellets-Auftragsposition vorhanden');
            return;
        }

        let einheit = this.pelletsAuftragsposition.Einheit;
        let menge = this.nettoGewicht;

        if (einheit == 'to' || einheit == 't') {
            menge = Utils.round(menge / 1000, 3);
        }

        this.pelletsAuftragsposition.Menge = menge;
        this.pelletsAuftragsposition.MengeIst = menge;
    }

    updateNetto() {
        log?.debug('updateNetto');

        this.nettoGewicht = 0;

        if (this.wiegung1?.Erfolgreich && this.wiegung2?.Erfolgreich) {
            let w1 = this.wiegung1.Gewicht || 0;
            let w2 = this.wiegung2.Gewicht || 0;

            // Beim Einblasen sollte das erste Gewicht immer größer sein als das zweite.
            // Es ist aber eigentlich nur die Differenz relevant. Eventuell funktioniert die Waage aber auch anders. Deshalb hier immer den Absolutwert bilden.
            this.nettoGewicht += Math.abs(w1 - w2);
        }


        if (this.wiegung3?.Erfolgreich && this.wiegung4?.Erfolgreich) {
            let w3 = this.wiegung3.Gewicht || 0;
            let w4 = this.wiegung4.Gewicht || 0;

            // Beim Einblasen sollte das erste Gewicht immer größer sein als das zweite.
            // Es ist aber eigentlich nur die Differenz relevant. Eventuell funktioniert die Waage aber auch anders. Deshalb hier immer den Absolutwert bilden.
            this.nettoGewicht += Math.abs(w3 - w4);
        }
    }

    async onWiegungLoeschen(nr: number) {
        log?.debug('onWiegungLoeschen: ' + nr);

        const ok = await UiHelper.confirmJaNein(nr + '. Wiegung wirklich löschen?');

        if (!ok) {
            return;
        }

        const wiegeung = this.getWiegung(nr);

        wiegeung.Erfolgreich = false;
        wiegeung.Datum = null;
        wiegeung.Text = null;
        wiegeung.Gewicht = null;
        wiegeung.Nummer = null;
        wiegeung.Typ = WiegungTyp.Wiegung;

        await this.speichern();

        this.cdr.detectChanges();
    }

    async speichern() {
        if (this.auftrag && this.auftrag.Positionen && this.auftrag.Positionen.length > 0) {
            const pos = this.auftrag.Positionen[0];

            pos.Wiegungen2 = [
                this.wiegung1,
                this.wiegung2,
                this.wiegung3,
                this.wiegung4
            ]

            this.auftrag.WiegungErfolgreich = (this.wiegung1?.Erfolgreich && this.wiegung2?.Erfolgreich) || (this.wiegung3?.Erfolgreich && this.wiegung4?.Erfolgreich);

            // Wenn jeweils nur die Erstwiegung erfolgt ist aber nicht die Zweitwiegung, dann ist die Wiegung nicht erfolgreich.
            if (this.wiegung1.Erfolgreich && !this.wiegung2.Erfolgreich) {
                this.auftrag.WiegungErfolgreich = false;
            }

            if (this.wiegung3.Erfolgreich && !this.wiegung4.Erfolgreich) {
                this.auftrag.WiegungErfolgreich = false;
            }

            await this.auftragService.speichereAuftrag(this.auftrag, { listItemAktualisieren: true });
        }
    }
}
